import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import { PROVIDER_ROLES, USER_ROLES } from "../utils/roleUtils";

// =============================================================================
// LAZY-ПЛЕЙСХОЛДЕРЫ ФИЧ-СТРАНИЦ
//
// ВНИМАНИЕ: перечисленные ниже файлы страниц ЕЩЁ НЕ СУЩЕСТВУЮТ — их создаёт
// другой разработчик (см. BUILD_BRIEF §Frontend). Это ОЖИДАЕМО: React.lazy
// разрешает import только при переходе на маршрут, поэтому шелл собирается и
// запускается даже без этих файлов. Пути и роли ниже — контракт для фич-девелопера.
//
//   Путь файла                                    Маршрут              Роль (RequireRole)
//   ------------------------------------------------------------------------------------
//   pages/Authentication/Login.js                 /login               — (auth, NonAuthLayout)
//   pages/Discovery/index.js                       /discovery           — (public, NonAuthLayout)
//   pages/Dashboard/index.js                       /dashboard           null (любой залогиненный)
//   pages/MySites/index.js                          /my-sites            null
//   pages/Organizations/index.js                   /organizations       null
//   pages/Organizations/OrganizationDetail.js      /organizations/:id   null
//   pages/Enrollment/index.js                      /enrollment          [hub-admin, org-admin]
//   pages/Licenses/index.js                        /licenses            [hub-admin, hub-support-engineer]
//   pages/Federation/index.js                      /federation          [hub-admin, org-admin]
//   pages/Support/index.js                         /support             null
//   pages/Support/TicketDetail.js                  /support/:id         null
//   pages/Maintenance/index.js                      /maintenance         null (создание — hub-admin)
// =============================================================================

// Auth (в NonAuthLayout)
const Login = lazy(() => import("../pages/Authentication/Login"));
const Logout = lazy(() => import("../pages/Authentication/Logout"));
// Discovery — публичный entry-flow сотрудника (до логина, в NonAuthLayout)
const Discovery = lazy(() => import("../pages/Discovery/index"));
const FindClinic = lazy(() => import("../pages/FindClinic/index"));
const InviteRedeem = lazy(() => import("../pages/FindClinic/InviteRedeem"));
const MyClinicsPage = lazy(() => import("../pages/FindClinic/MyClinics"));

// Protected feature pages
const Dashboard = lazy(() => import("../pages/Dashboard/index"));
const Organizations = lazy(() => import("../pages/Organizations/index"));
const OrganizationDetail = lazy(() => import("../pages/Organizations/OrganizationDetail"));
const Enrollment = lazy(() => import("../pages/Enrollment/index"));
const Connectors = lazy(() => import("../pages/Connectors/index"));
const Licenses = lazy(() => import("../pages/Licenses/index"));
const Federation = lazy(() => import("../pages/Federation/index"));
const Support = lazy(() => import("../pages/Support/index"));
const TicketDetail = lazy(() => import("../pages/Support/TicketDetail"));
const MySites = lazy(() => import("../pages/MySites/index"));
const Maintenance = lazy(() => import("../pages/Maintenance/index"));
// Новые страницы контроля (аудит UI-гэпов 2026-07-26)
const Fleet = lazy(() => import("../pages/Fleet/index"));
const SigningKeys = lazy(() => import("../pages/SigningKeys/index"));
const ReleaseTrust = lazy(() => import("../pages/ReleaseTrust/index"));
const DiagnosticBundles = lazy(() => import("../pages/DiagnosticBundles/index"));
const Profile = lazy(() => import("../pages/Profile/index"));
// Срез 3 (самообслуживание, ADR §7.8/§7.11)
const Provisioning = lazy(() => import("../pages/Provisioning/index"));
const ProvisioningDetail = lazy(() => import("../pages/Provisioning/ProvisioningDetail"));
const MyLicenses = lazy(() => import("../pages/MyLicenses/index"));
const NotFound = lazy(() => import("../pages/NotFound/index"));

// =============================================================================
// Матрица доступа по ролям (EyeHub)
//
//   null                  — любой аутентифицированный пользователь
//   PROVIDER_ROLES        — hub-admin + hub-support-engineer (сотрудники провайдера)
//   [hub-admin, org-admin]— provider-ops + администратор организации
// =============================================================================
const ENROLLMENT_ROLES = [USER_ROLES.HUB_ADMIN, USER_ROLES.ORG_ADMIN];
const FEDERATION_ROLES = [USER_ROLES.HUB_ADMIN, USER_ROLES.ORG_ADMIN];
const LICENSE_ROLES = [USER_ROLES.HUB_ADMIN]; // backend LicenseAdminViewSet = IsHubAdmin
const HUB_ADMIN_ONLY = [USER_ROLES.HUB_ADMIN]; // ключи подписи — только провайдер-админ
// Провижининг — backend AdminProvisioningOrderViewSet = IsHubAdmin. Поддержку сюда НЕ
// пускаем: retry пересоздаёт одноразовые секреты и запускает сагу с внешними
// эффектами (Keycloak, GitLab), а это провайдерское административное действие.
const PROVISIONING_ROLES = [USER_ROLES.HUB_ADMIN];
// Самообслуживание — backend SelfServiceLicenseViewSet = IsOrgAdmin, а IsOrgAdmin
// пропускает и hub-admin. Скоуп всё равно режется по своим организациям на бэкенде:
// у hub-admin своих организаций обычно нет, и он увидит пустую витрину — это верно,
// флотом он управляет через /licenses.
const SELF_SERVICE_ROLES = [USER_ROLES.ORG_ADMIN, USER_ROLES.HUB_ADMIN];

const userRoutes = [
  // Дашборд (fleet-обзор) — доступен всем аутентифицированным
  { path: "dashboard", component: Dashboard, roles: null },

  // Мои площадки (Ц12) — site-picker сотрудника (любой аутентифицированный)
  { path: "my-sites", component: MySites, roles: null },

  // Список клиник Hub-subject пациента (без PHI, только имя/город/статус)
  { path: "my-clinics", component: MyClinicsPage, roles: null },

  // Организации: список + деталь (домены/площадки/инсталляции)
  { path: "organizations", component: Organizations, roles: null },
  { path: "organizations/:id", component: OrganizationDetail, roles: null },

  // Enrollment коннекторов (создание/одобрение токенов)
  { path: "enrollment", component: Enrollment, roles: ENROLLMENT_ROLES },

  // Коннекторы (fleet-обзор identities) — сотрудники провайдера; revoke — hub-admin
  { path: "connectors", component: Connectors, roles: PROVIDER_ROLES },

  // Лицензии (EyeHub — issuer)
  { path: "licenses", component: Licenses, roles: LICENSE_ROLES },

  // Федерация (links + directory)
  { path: "federation", component: Federation, roles: FEDERATION_ROLES },

  // Поддержка: список тикетов + деталь
  { path: "support", component: Support, roles: null },
  { path: "support/:id", component: TicketDetail, roles: null },

  // Окна обслуживания (Ц13): список — любой аутентифицированный; создание
  // (кнопка/модалка) гейтится по hub-admin внутри страницы.
  { path: "maintenance", component: Maintenance, roles: null },

  // --- Контроль флота / лицензионных ключей / диагностики (аудит 2026-07-26) ---
  { path: "fleet", component: Fleet, roles: PROVIDER_ROLES },
  { path: "signing-keys", component: SigningKeys, roles: HUB_ADMIN_ONLY },
  { path: "release-trust", component: ReleaseTrust, roles: HUB_ADMIN_ONLY },
  { path: "diagnostic-bundles", component: DiagnosticBundles, roles: PROVIDER_ROLES },
  // --- Срез 3: самообслуживание (ADR §7.8 / §7.11) ---
  // Провижининг: операторский разбор заказов, вставших в needs_attention.
  { path: "provisioning", component: Provisioning, roles: PROVISIONING_ROLES },
  // Деталь — отдельный маршрут, а не модалка: ссылку на конкретный заказ оператор
  // передаёт в тикете, и она обязана открываться напрямую.
  { path: "provisioning/:id", component: ProvisioningDetail, roles: PROVISIONING_ROLES },
  // Мои лицензии: витрина потребления + переключатель перерасхода. Authority по
  // overage — хаб, поэтому переключатель живёт здесь, а не в админке клиники.
  { path: "my-licenses", component: MyLicenses, roles: SELF_SERVICE_ROLES },

  // Профиль: кто я и какие у меня роли (диагностика прав доступа)
  { path: "profile", component: Profile, roles: null },

  // Redirect с корня на дашборд
  { path: "", element: <Navigate to="dashboard" replace /> },
  { path: "*", component: NotFound, roles: null },
];

const authRoutes = [
  { path: "/login", component: Login },
  { path: "/logout", component: Logout },
  // Публичный entry-flow сотрудника (discovery по рабочему email) — до логина.
  { path: "/discovery", component: Discovery },
  // Лендинг пациента: поиск клиники и погашение инвайта (без PHI).
  { path: "/find", component: FindClinic },
  { path: "/i/:code", component: InviteRedeem },
  { path: "*", element: <Navigate to="/login" replace /> },
];

export { userRoutes, authRoutes };
